import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { User } from '@shared/schemas';
import { api } from '@/api';
import { supabase } from '@/api/supabaseClient';
import { installSessionKeepalive } from '@/api/sessionKeepalive';
import { assembleUser } from '@/api/endpoints/me';
import { StorageKeys, removeStorage } from '@/lib/localStorage';
import { clearLastScreen } from '@/lib/lastScreen';
import type { LoginBody, SignupBody, ResetPasswordBody } from '@/api/endpoints/auth';

/**
 * AuthContext · Supabase Auth engine (slot slot_supabase_migration · SM.3).
 *
 * The public `AuthContextValue` interface is unchanged from the Hono era —
 * the auth screens consume the same shape. Only the engine swapped: session
 * state is driven by `supabase.auth.onAuthStateChange`, and login / signup /
 * reset delegate to `api.auth.*` (supabase-js calls). The custom access-token
 * ref and the /api/auth/me bootstrap ping are retired — supabase-js owns the
 * token lifecycle (persist + auto-refresh).
 */

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthed: boolean;
  error: string | null;
  /** Re-read the current user from Supabase. */
  refreshMe: () => Promise<void>;
  /** Clears local user state (server logout uses `logout()`). */
  clearSession: () => void;
  /** Email/password login · sets user on success. */
  login: (body: LoginBody) => Promise<{ user: User } | { error: string }>;
  /** Email/password signup · 'pending_verification' (account-enumeration-safe). */
  signup: (body: SignupBody) => Promise<{ status: string } | { error: string }>;
  /** Sign out · clears Supabase session + local state. */
  logout: () => Promise<void>;
  /** Password-reset email · 'pending_email' (uniform response). */
  forgotPassword: (email: string) => Promise<{ status: string } | { error: string }>;
  /** Set new password on an active recovery session · sets user on success. */
  resetPassword: (body: ResetPasswordBody) => Promise<{ user: User } | { error: string }>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  /** Test seam · skips the Supabase auth subscription when explicitly false. */
  bootstrap?: boolean;
}

function errMsg(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * UAT-FINDING-CRIT (slot slot_logout_reset) · purge every per-user client-side
 * cache on logout.
 *
 * `ProgressProvider` is mounted *inside* `AuthGate` (App.tsx), so logout
 * (`setUser(null)`) unmounts it and the in-memory progress state is already
 * gone. The persistent leak vector is localStorage: the ADR-006 hydrate-cache
 * survives the unmount, so a login under a *different* identity on the same
 * browser context remounts ProgressProvider, reads the predecessor's
 * `qastudio.v1.progress`, and renders it under the new user (information
 * disclosure) — and any edit then PATCHes that stale snapshot back under the
 * new JWT (server-side data corruption).
 *
 * The two documented vectors are cleared explicitly via their owning modules
 * (decoupled from the literal key strings); the rest of the `qastudio.v1.*`
 * namespace (user · ui · pomodoro · quizAttempts) is swept for
 * belt-and-suspenders — all of it is per-user. Supabase auth tokens (`sb-*`)
 * are owned by `supabase.auth.signOut()` and left untouched.
 */
function purgeClientCaches(): void {
  if (typeof window === 'undefined') return;
  removeStorage(StorageKeys.progress); // ADR-006 hydrate-cache · primary leak vector
  clearLastScreen(); // FS lastScreen tracker
  try {
    const stale: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith('qastudio.')) stale.push(k);
    }
    stale.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // storage unavailable (private mode / quota) · best-effort
  }
}

export function AuthProvider({ children, bootstrap = true }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(bootstrap);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  /** Last authenticated user id · detects a cross-tab account swap (shared
   *  Supabase session) so we can purge the previous identity's caches. */
  const prevUserIdRef = useRef<string | null>(null);

  const refreshMe = useCallback(async () => {
    setError(null);
    try {
      const next = await api.me.get();
      if (mounted.current) setUser(next);
    } catch {
      if (mounted.current) setUser(null);
    }
  }, []);

  const clearSession = useCallback(() => {
    setUser(null);
  }, []);

  const login = useCallback(async (body: LoginBody) => {
    setError(null);
    try {
      const res = await api.auth.login(body);
      if (mounted.current) setUser(res.user);
      return { user: res.user };
    } catch (err) {
      const msg = errMsg(err, 'Не удалось войти');
      if (mounted.current) setError(msg);
      return { error: msg };
    }
  }, []);

  const signup = useCallback(async (body: SignupBody) => {
    setError(null);
    try {
      const res = await api.auth.signup(body);
      return { status: res.status };
    } catch (err) {
      const msg = errMsg(err, 'Не удалось создать аккаунт');
      if (mounted.current) setError(msg);
      return { error: msg };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      // best-effort · session may already be gone · clear locally regardless
    }
    if (mounted.current) setUser(null);
    // UAT-FINDING-CRIT · purge per-user caches so the next login on this browser
    // context cannot inherit the previous user's progress. Runs regardless of
    // the api.auth.logout() outcome (clear-locally-regardless contract above)
    // and regardless of mount state (it touches localStorage, not React state).
    purgeClientCaches();
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    setError(null);
    try {
      const res = await api.auth.forgotPassword({ email });
      return { status: res.status };
    } catch (err) {
      const msg = errMsg(err, 'Сетевая ошибка');
      if (mounted.current) setError(msg);
      return { error: msg };
    }
  }, []);

  const resetPassword = useCallback(async (body: ResetPasswordBody) => {
    setError(null);
    try {
      const res = await api.auth.resetPassword(body);
      if (mounted.current) setUser(res.user);
      return { user: res.user };
    } catch (err) {
      const msg = errMsg(err, 'Сетевая ошибка');
      if (mounted.current) setError(msg);
      return { error: msg };
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (!bootstrap) {
      setIsLoading(false);
      return () => {
        mounted.current = false;
      };
    }
    // Supabase drives session state. onAuthStateChange fires INITIAL_SESSION on
    // subscribe, then SIGNED_IN / SIGNED_OUT / TOKEN_REFRESHED / USER_UPDATED /
    // PASSWORD_RECOVERY. Any event carrying a session → (re)load the user.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      // Defer Supabase calls out of the callback (documented re-entrancy guard).
      setTimeout(() => {
        if (!mounted.current) return;
        if (session?.user) {
          const nextId = session.user.id;
          // Cross-tab account swap: Supabase keeps ONE session in shared
          // localStorage, so logging into account B in another tab silently
          // re-points this tab to B. Purge the previous identity's client caches
          // so B never inherits A's progress and A's optimistic edits can't be
          // PATCHed under B (UAT-FINDING-CRIT · multi-tab variant · 8 JUN 2026).
          if (prevUserIdRef.current && prevUserIdRef.current !== nextId) {
            purgeClientCaches();
          }
          prevUserIdRef.current = nextId;
          void assembleUser(session.user)
            .then((u) => {
              if (mounted.current) setUser(u);
            })
            .catch(() => {
              if (mounted.current) setUser(null);
            })
            .finally(() => {
              if (mounted.current) setIsLoading(false);
            });
        } else {
          prevUserIdRef.current = null;
          setUser(null);
          setIsLoading(false);
        }
      }, 0);
    });
    // Proactive token refresh on tab wake / focus so a session that lapsed
    // while the tab slept is refreshed before the next request burst fires.
    const removeKeepalive = installSessionKeepalive();
    return () => {
      mounted.current = false;
      sub.subscription.unsubscribe();
      removeKeepalive();
    };
  }, [bootstrap]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthed: user !== null,
      error,
      refreshMe,
      clearSession,
      login,
      signup,
      logout,
      forgotPassword,
      resetPassword,
    }),
    [user, isLoading, error, refreshMe, clearSession, login, signup, logout, forgotPassword, resetPassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within <AuthProvider>');
  }
  return ctx;
}

/**
 * Optional variant · returns null when called outside <AuthProvider>.
 * Use this for ambient contexts (e.g. ThemeProvider syncing via API) so
 * test fixtures that mount the provider standalone don't need to wrap
 * AuthProvider too. FS slot · 2026-05-31.
 */
export function useAuthContextOptional(): AuthContextValue | null {
  return useContext(AuthContext);
}
